import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import ChangePasswordModal from 'containers/ChangePasswordModal';
import DeleteAccountModal from 'containers/DeleteAccountModal';
import LocationPreference from 'components/LocationPreference';
import UserAvatar from 'components/UserAvatar';
import EditAvatarModal from 'components/UserAvatar/components/EditAvatarModal';
import SuccessAvatarModal from 'components/UserAvatar/components/SuccessAvatarModal';
import WinLossStatement from 'components/WinLossStatement';
import WinLossStatementMobile from 'components/WinLossStatementMobile';
import { LG_BREAKPOINT_AND_UP, LOCATION_DATA } from 'utils/constants';
import { formatAddress, formatGender } from 'utils/formatting';
import { US_STATE_ABBR } from 'utils/geographicData';
import useMediaQuery from 'utils/useMediaQuery';
import { DISPLAY_NAME, ONLINE_EMAIL } from '../../profileUtils';
import {
  DynamicPatronCard,
  ProfileAvatarCard,
  ProfileVenueCard,
  StaticPatronCard
} from '../PatronDetailCards';
import './ProfileForm.css';

const ProfileForm = ({
  user,
  userProfile,
  accountProfile,
  accountType,
  profileError,
  onEditPreferredLocation,
  onEdit,
  onSubmit,
  results
}) => {
  const isDesktop = useMediaQuery(LG_BREAKPOINT_AND_UP);

  const [editingOnDesktop, setEditingOnDesktop] = useState(false);
  const [onlineEmail, setOnlineEmail] = useState(
    userProfile ? userProfile.onlineEmail : ''
  );
  const [updatedEmail, setUpdatedEmail] = useState('');
  const [displayName, setDisplayName] = useState(
    userProfile ? userProfile.displayName : ''
  );
  const [showWStorePreferenceModal, setShowWStorePreferenceModal] = useState(
    userProfile && !userProfile.preferredLocation
  );
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showEditAvatar, setShowEditAvatar] = useState(false);
  const [showSuccessAvatar, setShowSuccessAvatar] = useState(false);
  const [showEditDisplayNameAndEmail, setShowEditDisplayNameAndEmail] =
    useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  useEffect(() => {
    if (
      results.find(({ name }) => name === ONLINE_EMAIL)?.success &&
      onlineEmail !== userProfile?.onlineEmail
    ) {
      setUpdatedEmail(onlineEmail);
      setOnlineEmail(userProfile?.onlineEmail);
    }
  }, [results, onlineEmail, userProfile.onlineEmail]);

  const trackProfilePageEvent = (eventName, eventValue, initiator) => {
    trackUserEvent({
      activityType: SELECTION_EVENT,
      eventName,
      eventValue,
      initiator
    });
  };

  const handleDesktopEditDisplayNameAndEmail = () => {
    trackProfilePageEvent(
      'desktop-edit-name-and-email',
      user,
      'edit-name-and-email-button'
    );

    setEditingOnDesktop(true);
    onEdit && onEdit();
  };

  const handleMobileEditDisplayNameAndEmail = () => {
    trackProfilePageEvent(
      'mobile-edit-name-and-email',
      user,
      'edit-name-and-email-button'
    );

    setShowEditDisplayNameAndEmail(true);
    onEdit && onEdit();
  };

  const handleEditPreferredLocation = () => {
    trackProfilePageEvent(
      'edit-preferred-location',
      user,
      'edit-preferred-location-button'
    );

    setShowWStorePreferenceModal(true);
  };

  const handleOnCloseSelectPreference = () => {
    trackProfilePageEvent(
      'close-preferred-location-modal',
      user,
      'close-preferred-location-modal-button'
    );

    setShowWStorePreferenceModal(false);
  };

  const handleOnSelectPreference = location => {
    onEditPreferredLocation && onEditPreferredLocation(location);
  };

  const handleInputChange = event => {
    if (event.target && event.target.name) {
      const { name, value } = event.target;
      if (name === 'displayName') {
        setDisplayName(value);
      } else if (name === ONLINE_EMAIL) {
        setOnlineEmail(value);
      }
    }
  };

  const handleCancel = () => {
    trackProfilePageEvent(
      'cancel-edit-name-and-email',
      user,
      'cancel-edit-name-and-email-button'
    );

    setEditingOnDesktop(false);
    setShowEditDisplayNameAndEmail(false);
    setOnlineEmail(userProfile.onlineEmail);
    setDisplayName(userProfile.displayName);
  };

  const handleSubmit = () => {
    trackProfilePageEvent(
      'submit-new-name-and-email',
      `displayName: ${displayName}, email: ${onlineEmail}`,
      'save-new-name-and-email-button'
    );

    onSubmit && onSubmit(displayName, onlineEmail);
  };

  const openStatementModal = () => {
    trackProfilePageEvent(
      'open-win-loss-statement',
      user,
      'open-win-loss-statement-button'
    );

    setShowStatementModal(true);
  };

  const closeStatementModal = () => {
    trackProfilePageEvent(
      'close-win-loss-statement',
      user,
      'close-win-loss-statement-button'
    );
    setShowStatementModal(false);
  };

  const handleEditAvatarData = show => {
    const event = show ? 'open-' : 'close-';
    trackProfilePageEvent(`${event}edit-avatar`, user, 'edit-avatar-button');
    setShowEditAvatar(show);
  };

  const handleEditPassword = () => {
    trackProfilePageEvent('edit-password', user, 'change-password-button');

    setShowEditPassword(true);
  };

  const handleDeleteAccountClicked = () => {
    trackProfilePageEvent('delete-account', user, 'delete-account-button');

    setShowDeleteAccount(true);
  };

  const getErrorMessage = resultName =>
    results.find(result => result.name === resultName && !result.success)
      ?.message || '';

  const renderProfileAvatar = () => {
    const avatarSelected = userProfile && userProfile.avatarData;
    return (
      <React.Fragment>
        <ProfileAvatarCard
          onEdit={() => {
            handleEditAvatarData(true);
          }}
          avatarData={
            avatarSelected ? (
              <UserAvatar
                avatarData={userProfile && userProfile.avatarData}
                withBackground={true}
              />
            ) : null
          }
        />
        <EditAvatarModal
          showEditAvatarModal={showEditAvatar}
          hideEditAvatar={() => {
            handleEditAvatarData(false);
          }}
          showSuccessAvatar={() => {
            setShowSuccessAvatar(true);
          }}
        />
        <SuccessAvatarModal
          showSuccessAvatarModal={showSuccessAvatar}
          hideSuccessAvatar={() => {
            setShowSuccessAvatar(false);
          }}
        />
      </React.Fragment>
    );
  };

  const renderPreferredLocation = userProfile => {
    const preferredData =
      userProfile.preferredLocation &&
      LOCATION_DATA[userProfile.preferredLocation];

    return (
      <ProfileVenueCard
        venueData={preferredData}
        onEdit={handleEditPreferredLocation}
      />
    );
  };

  const renderCustomizableDetails = userProfile => {
    const displayNameErrorMessage = getErrorMessage(DISPLAY_NAME);
    const emailErrorMessage = getErrorMessage(ONLINE_EMAIL);

    const emailResult = results.find(({ name }) => name === ONLINE_EMAIL);
    const emailChangedDetail = emailResult?.success
      ? {
          message: emailResult.message,
          onlineEmail: updatedEmail
        }
      : {};

    const details = [
      {
        label: 'Display Name',
        value: displayName,
        name: DISPLAY_NAME,
        id: 'displayNameForm',
        errorMessage: displayNameErrorMessage
      },
      {
        label: 'Email',
        value: onlineEmail,
        name: ONLINE_EMAIL,
        id: 'emailForm',
        errorMessage: emailErrorMessage,
        emailChanged: emailChangedDetail
      }
    ];

    return (
      <DynamicPatronCard
        details={details}
        onlineJoinDate={userProfile.onlineJoinDate}
        onEdit={
          isDesktop
            ? handleDesktopEditDisplayNameAndEmail
            : handleMobileEditDisplayNameAndEmail
        }
        editingOnDesktop={editingOnDesktop}
        showEditModal={showEditDisplayNameAndEmail}
        onChange={handleInputChange}
        renderButtons={() => renderButtons(userProfile)}
        closeEditModal={handleCancel}
        isDesktop={isDesktop}
        handleSubmit={handleSubmit}
        displayName={displayName}
        onlineEmail={onlineEmail}
      />
    );
  };

  const renderLocationPreference = userProfile => {
    return (
      <LocationPreference
        user={user}
        onSelectPreference={handleOnSelectPreference}
        onClose={handleOnCloseSelectPreference}
        currSelectedPreference={userProfile.preferredLocation}
        show={showWStorePreferenceModal}
      />
    );
  };

  const renderButtons = userProfile => {
    return (
      <div className="profile-button-container">
        <div className="profile-cancel">
          <Button
            className="profile-cancel-btn"
            variant="blue"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
        <div className="profile-save">
          <Button
            className="profile-save-btn"
            variant="orange"
            onClick={handleSubmit}
            disabled={
              !onlineEmail ||
              !displayName ||
              (userProfile.onlineEmail === onlineEmail &&
                userProfile.displayName === displayName)
            }
          >
            Save
          </Button>
        </div>
      </div>
    );
  };

  const renderProfileDetailsContainer = () => {
    let content = null;
    if (profileError) {
      content = (
        <div className="alert alert-danger">
          There was a problem retrieving your rewards profile data. If this
          continues, please contact support.
        </div>
      );
    } else if (!accountProfile) {
      content = (
        <div className="profile-patron-unregistered">
          Rewards Account Not Registered
        </div>
      );
    } else {
      const stateAbbr =
        US_STATE_ABBR[accountProfile.addressState] ||
        accountProfile.addressState;
      const rewardsJoinDate = moment(
        accountProfile.rewardsJoinDate,
        'MM/DD/YYYY'
      );
      const joinYear = Math.max(2018, rewardsJoinDate.year());
      const profileDetails = [
        {
          details: [
            { label: 'First Name', value: accountProfile.firstName },
            { label: 'Last Name', value: accountProfile.lastName }
          ]
        },
        {
          details: [
            { label: 'Birthday', value: accountProfile.birthday },
            { label: 'Gender', value: formatGender(accountProfile.gender) }
          ]
        },
        {
          isFlex: true,
          isRow: true,
          header: 'Telephone Number',
          details: [
            { label: 'M', value: accountProfile.mobilePhone },
            { label: 'H', value: accountProfile.homePhone }
          ]
        },
        {
          header: 'Address',
          details: [
            {
              label: '',
              value: formatAddress(
                accountProfile.addressLine1,
                accountProfile.addressLine2,
                accountProfile.addressCity,
                stateAbbr,
                accountProfile.addressZip
              ).join(', ')
            }
          ]
        },
        {
          details: [{ label: 'Rewards Email', value: accountProfile.email }]
        },
        {
          details: [
            {
              label: 'Rewards Join Date',
              value: accountProfile.rewardsJoinDate
            }
          ]
        }
      ];
      content = (
        <div className="patron-panels">
          <div className="patron-left-panel">
            {renderProfileAvatar()}
            {renderCustomizableDetails(userProfile)}
            {renderPreferredLocation(userProfile)}
            {renderLocationPreference(userProfile)}
            <Button
              className="change-password-btn"
              variant="blue"
              onClick={handleEditPassword}
            >
              Change Password
            </Button>
            <ChangePasswordModal
              showEditPassword={showEditPassword}
              closePasswordModal={() => setShowEditPassword(false)}
            />
          </div>

          <div className="patron-right-panel">
            <StaticPatronCard userDetails={profileDetails} />
            <Button
              className="win-loss-statement-button"
              variant="blue"
              onClick={openStatementModal}
            >
              Win/Loss Statement
            </Button>
            {isDesktop ? (
              <WinLossStatement
                accountType={accountType}
                handleClose={closeStatementModal}
                show={showStatementModal}
                firstName={accountProfile.firstName}
                lastName={accountProfile.lastName}
                accountNumber={accountProfile.patronAccountNumber}
                joinYear={joinYear}
              />
            ) : (
              <WinLossStatementMobile
                accountType={accountType}
                handleClose={closeStatementModal}
                show={showStatementModal}
                firstName={accountProfile.firstName}
                lastName={accountProfile.lastName}
                accountNumber={accountProfile.patronAccountNumber}
                joinYear={joinYear}
              />
            )}
            <button
              className="delete-account-button"
              type="button"
              onClick={handleDeleteAccountClicked}
            >
              Delete Online Account
            </button>
            <DeleteAccountModal
              showDeleteAccount={showDeleteAccount}
              closeModal={() => setShowDeleteAccount(false)}
            />
          </div>
        </div>
      );

      return (
        <div className="profile-patron-container">
          <h2 className="sr-only">Rewards Account Profile</h2>
          {content}
        </div>
      );
    }
  };

  return <div className="profile-form">{renderProfileDetailsContainer()}</div>;
};

ProfileForm.propTypes = {
  userProfile: PropTypes.shape({
    displayName: PropTypes.string,
    onlineEmail: PropTypes.string,
    avatarUrl: PropTypes.string,
    preferredLocation: PropTypes.string,
    onlineJoinDate: PropTypes.string,
    avatarData: PropTypes.string
  }),
  patronProfile: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    birthday: PropTypes.string,
    rewardsJoinDate: PropTypes.string,
    gender: PropTypes.string,
    cmpEmail: PropTypes.string,
    mobilePhone: PropTypes.string,
    homePhone: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressCity: PropTypes.string,
    addressState: PropTypes.string,
    addressZip: PropTypes.string
  }),
  patronProfileError: PropTypes.any,
  onEditPreferredLocation: PropTypes.func,
  onEdit: PropTypes.func,
  onSubmit: PropTypes.func,
  results: PropTypes.arrayOf(PropTypes.object)
};

export default ProfileForm;
